import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BusyPeriodsList } from "@/components/admin/BusyPeriodsList";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { Footer } from "@/components/ui/footer";
import { useDashboardData } from "@/hooks/useDashboardData";
import { FullScreenLoader } from "@/components/ui/full-screen-loader";

export default function AllEvents() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isAdmin, isProfileLoading } = useDashboardData();

  const { data: busyPeriods, isLoading } = useQuery({
    queryKey: ["busy_periods_all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("busy_periods")
        .select("*")
        .order("start_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("busy_periods")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Event deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["busy_periods_all"] });
      queryClient.invalidateQueries({ queryKey: ["busy_periods_current_year"] });
      queryClient.invalidateQueries({ queryKey: ["busy_periods"] });
    } catch (error) {
      console.error("Error deleting event:", error);
      toast({
        title: "Error deleting event",
        description: "Make sure you have manager permissions",
        variant: "destructive",
      });
    }
  };

  if (isProfileLoading) return <FullScreenLoader />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1" style={{
        background: 'linear-gradient(109.6deg, rgba(223,234,247,1) 11.2%, rgba(244,248,252,1) 91.1%)'
      }}>
        <div className="max-w-7xl mx-auto px-8 py-10 space-y-6">
          <Button variant="ghost" onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>

          <Card className="hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle>All Events</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div>Loading...</div>
              ) : busyPeriods && busyPeriods.length > 0 ? (
                <BusyPeriodsList busyPeriods={busyPeriods} handleDelete={handleDelete} />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No events found
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
